/**
 * Page composition.
 *
 * v2 is one question with two answers: a knob at the top, two always-visible
 * columns beneath it, then three dedicated sections for the questions that are
 * not about money, then the working.
 *
 * The order is deliberate. Money first because it is what most readers came
 * for, water next because it is the most contested local fact, noise last of
 * the three because it is the finding that runs against the project and burying
 * it lower would be a choice about who gets to see it.
 */

import type { Pack } from '../engine/schema.js';
import { assumptions } from './assumptions.js';
import { comparison } from './compare.js';
import { createFootnotes } from './footnotes.js';
import { knob } from './knob.js';
import { esc } from './num.js';
import {
  abundanceExplainer,
  outcomePicker,
  outcomeSummary,
  selectedOutcomes,
} from './outcomes.js';
import { airSection, noiseSection, waterSection } from './sections.js';
import { buildScope } from '../engine/scope.js';
import type { AppState } from './store.js';
import { renderDrawer } from './drawer.js';

export function renderApp(pack: Pack, state: AppState, packs: readonly Pack[] = [pack]): string {
  const built = pack.scenarios.find((s) => !s.lowConfidence) ?? pack.scenarios[0]!;
  const notBuilt = pack.scenarios.find((s) => s.id !== built.id) ?? built;

  const scope = buildScope(pack, {
    scenarioId: state.scenarioId,
    params: {},
    overrides: state.overrides,
  });

  // One registry per render. The assumptions section is built from whatever the
  // page above it actually cited, so a marker without an entry is impossible.
  const fn = createFootnotes();

  // Chosen outcomes pull their section forward. Nothing is dropped, so the
  // finding that runs against the project stays on the page whatever is picked.
  const wanted = new Set(selectedOutcomes(state).map((o) => o.anchor));
  const features: [string, string][] = [
    ['water', waterSection(pack, state, fn)],
    ['air', airSection(pack, state, fn)],
    ['noise', noiseSection(pack, state, fn)],
  ];
  const ordered = [
    ...features.filter(([id]) => wanted.has(id)),
    ...features.filter(([id]) => !wanted.has(id)),
  ];

  const body = [
    outcomeSummary(pack, state, fn, built, notBuilt),
    comparison(pack, state, fn, built, notBuilt),
    ...ordered.map(([, html]) => html),
  ].join('');

  return `
  ${header(pack, packs)}
  ${hero(pack, state)}
  ${knob({
    question: 'Pine Island gets built as proposed',
    yesId: built.id,
    noId: notBuilt.id,
    yesLabel: 'Yes',
    noLabel: 'No',
    current: state.scenarioId,
  })}
  ${counterfactual(pack, state)}
  ${outcomePicker(pack, state)}
  <main>
    ${body}
    ${abundanceExplainer()}
    ${assumptions(pack, state, fn)}
    ${methodology(pack)}
  </main>
  ${footer(pack)}
  ${scope.modified.length ? overrideStrip(scope.modified.length) : ''}
  ${state.openDrawer ? renderDrawer({ pack, state }, state.openDrawer) : ''}`;
}

// ---------------------------------------------------------------- chrome

function header(pack: Pack, packs: readonly Pack[]): string {
  return `<header class="site-header"><div class="container brand">
    <span class="eyebrow">${esc(pack.meta.publisher)}</span>
    ${projectSwitcher(pack, packs)}
    <span class="eyebrow">Data version ${pack.meta.dataVersion} · updated ${esc(
      pack.meta.lastUpdated,
    )}</span>
  </div></header>`;
}

/** Hidden while there is only one jurisdiction — a switcher with one option is noise. */
function projectSwitcher(pack: Pack, packs: readonly Pack[]): string {
  if (packs.length < 2) return '';
  const buttons = packs
    .map(
      (p) =>
        `<button data-project="${esc(p.meta.slug)}" aria-pressed="${
          p.meta.slug === pack.meta.slug
        }">${esc(p.meta.place)}</button>`,
    )
    .join('');
  return `<nav class="project-switcher" aria-label="Choose a project">${buttons}</nav>`;
}

function hero(pack: Pack, state: AppState): string {
  const n = pack.narrative;
  const stale =
    state.linkedDataVersion !== null && state.linkedDataVersion < pack.meta.dataVersion;
  return `<section class="hero"><div class="container">
    <h1 class="display">${esc(n.headline ?? '')}</h1>
    <p class="lede">${esc(n.subhead ?? '')}</p>
    <p class="premise">${esc(n.premise ?? '')}</p>
    ${
      stale
        ? `<div class="version-banner">This link was made against data version ${state.linkedDataVersion}. Some numbers have been corrected since. <a href="#methodology">What changed</a>.</div>`
        : ''
    }
  </div></section>`;
}

/**
 * The counterfactual, immediately under the knob and above the numbers.
 *
 * It sits here rather than in the methodology because every figure in the
 * column below inherits it. A reader who does not know what "not built" is
 * assumed to mean cannot evaluate a single number on the page.
 */
function counterfactual(pack: Pack, state: AppState): string {
  const s = pack.scenarios.find((x) => x.id === state.scenarioId);
  if (!s) return '';
  return `<section class="counterfactual-bar"><div class="container">
    <p class="prose"><strong>What that means:</strong> ${esc(s.counterfactual)}</p>
  </div></section>`;
}

function overrideStrip(count: number): string {
  return `<div class="override-strip"><div class="container">
    <span>You have changed ${count} assumption${count === 1 ? '' : 's'}. The figures are no longer the published ones.</span>
    <button data-reset-all>Back to published values</button>
  </div></div>`;
}

// ---------------------------------------------------------------- methodology

function methodology(pack: Pack): string {
  const n = pack.narrative;
  const blocks = [
    ['Where the project stands today', n.onStatus],
    ['On the effect on electricity bills', n.onRates],
    ['On the emissions comparison', n.onEmissions],
    ['On clean energy and investment', n.onCleanEnergy],
    ['On what Minnesota actually taxes', n.onTaxBase],
    ['What is not included', n.limitations],
    ['When a number turns out to be wrong', n.correctionPolicy],
  ] as const;

  return `<section class="methodology" id="methodology">
    <div class="container">
      <div class="category-head"><h2 class="section-title">Methodology and limits</h2></div>
      <div class="method-grid">
        ${blocks
          .filter(([, body]) => Boolean(body))
          .map(
            ([title, body]) =>
              `<div><span class="eyebrow">${esc(title)}</span><p class="prose" style="margin-top:8px">${esc(
                body ?? '',
              )}</p></div>`,
          )
          .join('')}
      </div>
    </div>
  </section>`;
}

function footer(pack: Pack): string {
  return `<footer class="site-footer"><div class="container">
    <p class="prose" style="font-size:13px">${esc(pack.meta.publisherNote)}</p>
    <p class="prose" style="font-size:13px;margin-top:var(--s2)">${esc(pack.meta.name)} · ${esc(
      pack.meta.place,
    )} · data version ${pack.meta.dataVersion}, ${esc(pack.meta.lastUpdated)}</p>
  </div></footer>`;
}
